import React from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * ============================================================================
 *  TG ADMIN — SHARED DESIGN SYSTEM  (minimal, near-monochrome)
 * ----------------------------------------------------------------------------
 *  A single token-driven system that gives the whole admin panel a calm,
 *  minimal, "proper" look — Linear / Stripe territory — in BOTH themes:
 *    • Near-monochrome: mostly neutral grays; the TG teal accent appears ONLY
 *      on the primary action, the active nav item, and focus rings.
 *    • Flat surfaces, hairline borders, one soft shadow, tight-but-breathable
 *      spacing. Type + spacing carry the hierarchy, not color and boxes.
 *    • Quiet status (dot + muted label), not loud pills.
 *    • Motion you feel but don't see (150–200ms ease).
 *
 *  Two layers, one source of truth:
 *    1. `AdminThemeProvider` — AntD ConfigProvider tokens (colorPrimary teal,
 *       minimal radii, neutral surfaces) so real AntD components (Table,
 *       Button, Input, Select, Segmented, Card, Statistic, Badge) render on-brand.
 *    2. `AdminDesignSystem` — a scoped `.tg-admin-root` stylesheet for the fine
 *       polish AntD tokens can't express, and for the shared Tailwind primitives
 *       the legacy markup still uses.
 *
 *  Purely additive to logic. Scoped under `.tg-admin-root` so it never leaks.
 * ============================================================================
 */

export const ADMIN_ROOT = 'tg-admin-root';

/** Minimal near-monochrome tokens. Mirrors the approved HTML preview. */
export const getAdminTokens = (isDark) =>
  isDark
    ? {
        // soft floating-shell look, dark variant
        stage: 'linear-gradient(135deg,#0e1016 0%,#12101a 50%,#0c1413 100%)',
        shell: '#14161c',
        bg: '#14161c', panel: '#171a22', panel2: '#1b1f28',
        sidebar: '#14161c', sidebarBorder: '#1e222b',
        border: '#252a35', border2: '#2c323e', hair: '#1e222b',
        text: '#f0f2f6', text2: '#a3abba', text3: '#6b7280',
        navText: '#a3abba', navIcon: '#7c8494', navHead: '#7a828f',
        accent: '#00D1C1', accent2: '#00E6D4', accentWeak: 'rgba(0,209,193,0.14)', accentTx: '#3fe0d2',
        sh: '0 2px 8px rgba(0,0,0,0.3)',
        shSoft: '0 14px 34px -14px rgba(0,0,0,0.55)',
        shLift: '0 22px 46px -16px rgba(0,0,0,0.65)',
        okTx: '#3fc27f', warnTx: '#e0b04a', badTx: '#f0736c', neutralTx: '#8a8a94',
        dotOk: '#3fc27f', dotInfo: '#7aa5f7', dotWarn: '#e0b04a', dotBad: '#f0736c', dotOff: '#6b7280',
        blueBg: 'rgba(47,111,237,0.14)', blueTx: '#7aa5f7',
        purpleBg: 'rgba(107,78,240,0.16)', purpleTx: '#a892f8',
        orangeBg: 'rgba(232,121,43,0.14)', orangeTx: '#f0a066',
        tealBg: 'rgba(0,209,193,0.14)', tealTx: '#3fe0d2',
      }
    : {
        // soft floating-shell look, light variant (Unflip-inspired)
        stage: 'linear-gradient(135deg,#eef2fc 0%,#f4f1fb 45%,#e9f8f5 100%)',
        shell: '#ffffff',
        bg: '#ffffff', panel: '#ffffff', panel2: '#fafbfc',
        sidebar: '#ffffff', sidebarBorder: '#f4f5f8',
        border: '#eef0f4', border2: '#e6e8ee', hair: '#f4f5f8',
        text: '#1a1d29', text2: '#5b6472', text3: '#9aa1b0',
        navText: '#5b6472', navIcon: '#9096a0', navHead: '#9aa1b0',
        accent: '#00A89B', accent2: '#00D1C1', accentWeak: '#e2f7f4', accentTx: '#00877d',
        sh: '0 2px 8px rgba(28,32,55,0.04)',
        shSoft: '0 10px 30px -12px rgba(28,32,55,0.12)',
        shLift: '0 18px 40px -14px rgba(28,32,55,0.20)',
        okTx: '#1aa363', warnTx: '#c07d17', badTx: '#e5514b', neutralTx: '#71717a',
        dotOk: '#1aa363', dotInfo: '#2f6fed', dotWarn: '#e0a020', dotBad: '#e5514b', dotOff: '#9aa1b0',
        blueBg: '#e8f1fe', blueTx: '#2f6fed',
        purpleBg: '#efeafe', purpleTx: '#6b4ef0',
        orangeBg: '#fdeee4', orangeTx: '#e8792b',
        tealBg: '#e2f7f4', tealTx: '#00877d',
      };

/** AntD ConfigProvider theme derived from the minimal tokens. */
export const getAntdTheme = (isDark) => {
  const t = getAdminTokens(isDark);
  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: t.accent,
      colorInfo: t.accent,
      colorBgContainer: t.panel,
      colorBgElevated: t.panel,
      colorBorder: t.border2,
      colorBorderSecondary: t.hair,
      colorText: t.text,
      colorTextSecondary: t.text2,
      colorTextTertiary: t.text3,
      colorTextPlaceholder: t.text3,
      borderRadius: 12,
      borderRadiusLG: 16,
      borderRadiusSM: 9,
      controlHeight: 40,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      boxShadow: t.sh,
      boxShadowSecondary: t.sh,
    },
    components: {
      Table: {
        headerBg: 'transparent',
        headerColor: t.text2,
        headerSplitColor: 'transparent',
        rowHoverBg: t.panel2,
        borderColor: t.hair,
        cellPaddingBlock: 16,
        cellPaddingInline: 16,
        cellFontSize: 14,
      },
      Button: { primaryShadow: 'none', defaultShadow: 'none', fontWeight: 600, borderRadius: 12 },
      Segmented: { itemSelectedBg: t.accent, itemSelectedColor: '#ffffff', trackBg: t.panel, borderRadius: 12 },
      Card: { paddingLG: 22, borderRadiusLG: 20 },
      Input: { borderRadius: 14, controlHeight: 42 },
      Select: { borderRadius: 14, controlHeight: 42 },
    },
  };
};

/** Wrap admin content so all AntD components inherit the minimal theme. */
export const AdminThemeProvider = ({ children }) => {
  const { mode } = useTheme();
  return <ConfigProvider theme={getAntdTheme(mode === 'dark')}>{children}</ConfigProvider>;
};

export const getAdminDesignSystem = (isDark) => {
  const t = getAdminTokens(isDark);
  return `
  /* ===== keyframes ===== */
  @keyframes tgRise { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
  @keyframes tgFade { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
  @keyframes tgGrow { from { width:0; } to { width:40px; } }

  /* ===== soft floating shell over a gradient stage ===== */
  .tg-admin-root {
    background: ${t.stage} !important;
    color: ${t.text};
    -webkit-font-smoothing: antialiased;
    letter-spacing: -0.01em;
    padding: 14px !important;
    gap: 0 !important;
  }
  /* paint the gradient across the WHOLE viewport (behind the shell) so no
     dark app-body color can peek through at the top/edges */
  .tg-admin-root::before {
    content: '';
    position: fixed;
    inset: 0;
    z-index: -1;
    background: ${t.stage};
  }
  .tg-admin-root .tg-shell {
    background: ${t.shell} !important;
    border-radius: 24px !important;
    overflow: hidden !important;
    box-shadow: ${t.shSoft} !important;
    display: flex; flex: 1; min-height: 0;
  }
  .tg-admin-root ::selection { background: ${t.accentWeak}; }
  .tg-admin-root ::-webkit-scrollbar { width: 9px; height: 9px; }
  .tg-admin-root ::-webkit-scrollbar-thumb { background: ${t.border2}; border-radius: 9px; border: 2px solid transparent; background-clip: padding-box; }
  .tg-admin-root ::-webkit-scrollbar-track { background: transparent; }

  /* ===== SIDEBAR ===== */
  .tg-admin-root .tg-sidebar { background: ${t.sidebar} !important; border-right: 1px solid ${t.sidebarBorder} !important; }
  .tg-admin-root .tg-sidebar-brand { border-bottom: 1px solid ${t.hair}; }
  .tg-admin-root .tg-sidebar-brand-name { color: ${t.text} !important; letter-spacing: -0.03em; font-size: 16px !important; font-weight: 800 !important; }
  .tg-admin-root .tg-sidebar-brand-sub { color: ${t.text3} !important; font-size: 11px !important; }
  .tg-admin-root .tg-sidebar-mark { background: ${t.panel} !important; border: 1px solid ${t.border} !important; box-shadow: ${t.sh} !important; border-radius: 11px !important; }
  .tg-admin-root .tg-nav-section-header { color: ${t.navHead} !important; cursor: pointer; font-size: 11px !important; font-weight: 700 !important; letter-spacing: 0.06em !important; padding: 14px 12px 5px !important; transition: color .16s ease; }
  .tg-admin-root .tg-nav-section-header:hover { color: ${t.text} !important; }
  .tg-admin-root .tg-nav-section-header svg { color: ${t.navHead} !important; opacity: .7; transition: transform .28s cubic-bezier(.34,1.4,.5,1); }
  .tg-admin-root .tg-nav-items { display: grid; grid-template-rows: 1fr; opacity: 1; transition: grid-template-rows .32s cubic-bezier(.4,0,.2,1), opacity .22s ease; }
  .tg-admin-root .tg-nav-items.is-collapsed { grid-template-rows: 0fr; opacity: 0; }
  .tg-admin-root .tg-nav-items-inner { overflow: hidden; min-height: 0; }
  .tg-admin-root .tg-nav-item {
    color: ${t.navText} !important;
    border-radius: 12px !important;
    font-size: 14px !important;
    line-height: 1.2 !important;
    font-weight: 500 !important;
    padding: 10px 12px !important;
    gap: 12px !important;
    transition: background .2s ease, color .2s ease, transform .18s cubic-bezier(.34,1.4,.5,1), box-shadow .2s ease;
  }
  .tg-admin-root .tg-nav-item span { color: inherit !important; }
  .tg-admin-root .tg-nav-item svg { color: ${t.navIcon} !important; opacity: .8; width: 19px !important; height: 19px !important; transition: transform .18s ease; }
  .tg-admin-root .tg-nav-item:hover { background: ${t.hair} !important; color: ${t.text} !important; transform: translateX(3px); }
  .tg-admin-root .tg-nav-item:hover svg { color: ${t.text} !important; transform: scale(1.12); }
  .tg-admin-root .tg-nav-item.is-active {
    background: ${t.accentWeak} !important;
    color: ${t.accentTx} !important;
    font-weight: 600 !important;
    box-shadow: none !important;
    transform: none !important;
  }
  .tg-admin-root .tg-nav-item.is-active svg { color: ${t.accentTx} !important; opacity: 1; }
  .tg-admin-root .tg-nav-badge { margin-left: auto; font-size: 11px; font-weight: 600; color: ${t.text3}; }
  .tg-admin-root .tg-nav-item.is-active .tg-nav-badge { color: ${t.accentTx}; opacity: .9; }
  /* sidebar footer */
  .tg-admin-root .tg-sidebar-foot { border-top: 1px solid ${t.sidebarBorder} !important; }
  .tg-admin-root .tg-sidebar-avatar {
    background: linear-gradient(135deg, ${t.accent}, ${t.purpleTx}) !important;
    color: #ffffff !important; font-weight: 700;
  }
  .tg-admin-root .tg-sidebar-user b { color: ${t.text} !important; }
  .tg-admin-root .tg-sidebar-user span { color: ${t.text3} !important; }
  .tg-admin-root .tg-sidebar-logout { color: ${t.text3} !important; transition: .16s; }
  .tg-admin-root .tg-sidebar-logout:hover { background: ${t.hair} !important; color: ${t.badTx} !important; }

  /* ===== main content ===== */
  .tg-admin-root main { background: ${t.shell} !important; }
  .tg-admin-root main h1.text-2xl { color: ${t.text} !important; letter-spacing: -0.035em; font-weight: 800; font-size: 28px; }
  .tg-admin-root .text-white { color: ${t.text} !important; }
  .tg-admin-root .text-gray-400, .tg-admin-root .text-gray-500 { color: ${t.text2} !important; }
  .tg-admin-root h2.text-lg, .tg-admin-root h2.text-\\[15px\\] { color: ${t.text} !important; font-weight: 700; letter-spacing: -0.02em; }

  /* legacy Tailwind card surfaces → clean panel */
  .tg-admin-root .bg-white\\/5, .tg-admin-root .bg-white\\/10 { background: ${t.panel} !important; border-color: ${t.border} !important; box-shadow: ${t.sh}; }
  .tg-admin-root .border-white\\/10, .tg-admin-root .border-white\\/20 { border-color: ${t.border} !important; }
  .tg-admin-root .rounded-lg { border-radius: 16px; }
  .tg-admin-root .rounded-xl { border-radius: 20px; }

  /* ===== KPI cards — pastel multi-color, big colored numbers, trend chip ===== */
  .tg-kpis { display:grid; grid-template-columns:repeat(4,1fr); gap:18px; margin-bottom:26px; }
  @media (max-width:1100px){ .tg-kpis{ grid-template-columns:repeat(2,1fr); } }
  .tg-kpi { border-radius:20px; padding:22px 24px; animation:tgRise .5s cubic-bezier(.2,.8,.2,1) both; transition:transform .25s cubic-bezier(.34,1.4,.5,1), box-shadow .25s ease; }
  .tg-kpi:hover { transform:translateY(-5px); box-shadow:${t.shLift}; }
  .tg-kpi:nth-child(1){animation-delay:.02s}.tg-kpi:nth-child(2){animation-delay:.08s}.tg-kpi:nth-child(3){animation-delay:.14s}.tg-kpi:nth-child(4){animation-delay:.2s}
  .tg-kpi.c-blue{background:${t.blueBg}} .tg-kpi.c-purple{background:${t.purpleBg}} .tg-kpi.c-orange{background:${t.orangeBg}} .tg-kpi.c-teal{background:${t.tealBg}}
  .tg-kpi .l { font-size:15px; font-weight:700; color:${t.text}; }
  .tg-kpi .row { display:flex; align-items:center; gap:16px; margin-top:14px; }
  .tg-kpi .v { font-size:30px; font-weight:800; letter-spacing:-.04em; line-height:1; }
  .tg-kpi.c-blue .v{color:${t.blueTx}} .tg-kpi.c-purple .v{color:${t.purpleTx}} .tg-kpi.c-orange .v{color:${t.orangeTx}} .tg-kpi.c-teal .v{color:${t.tealTx}}
  .tg-kpi .v small { font-size:13px; font-weight:500; color:${t.text3}; margin-left:4px; }
  .tg-kpi .div { width:1px; height:32px; background:${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'}; }
  .tg-kpi .imp { font-size:13.5px; color:${t.text2}; display:flex; align-items:center; gap:9px; font-weight:500; }
  .tg-kpi .arw { height:26px; width:26px; border-radius:50%; background:${t.panel}; display:flex; align-items:center; justify-content:center; box-shadow:${t.sh}; flex-shrink:0; }
  .tg-kpi .arw svg { width:13px; height:13px; }
  .tg-kpi.c-blue .arw svg{color:${t.blueTx}} .tg-kpi.c-purple .arw svg{color:${t.purpleTx}} .tg-kpi.c-orange .arw svg{color:${t.orangeTx}} .tg-kpi.c-teal .arw svg{color:${t.tealTx}}

  /* ===== inputs / selects / buttons ===== */
  .tg-admin-root input[type="text"], .tg-admin-root input[type="email"], .tg-admin-root input[type="number"],
  .tg-admin-root input[type="search"], .tg-admin-root textarea, .tg-admin-root select,
  .tg-admin-root .ant-input-affix-wrapper, .tg-admin-root .ant-input, .tg-admin-root .ant-select-selector {
    border-radius:14px !important; border-color:${t.border} !important; background:${t.panel} !important; color:${t.text} !important;
    transition: border-color .18s ease, box-shadow .18s ease !important;
  }
  .tg-admin-root input::placeholder, .tg-admin-root textarea::placeholder { color:${t.text3} !important; }
  .tg-admin-root input:focus, .tg-admin-root textarea:focus, .tg-admin-root select:focus,
  .tg-admin-root .ant-input-affix-wrapper:focus-within, .tg-admin-root .ant-select-focused .ant-select-selector {
    outline:none !important; border-color:${t.accent} !important; box-shadow:0 0 0 4px ${t.accentWeak} !important;
  }
  .tg-admin-root .ant-btn-primary { background:${t.accent} !important; border:none !important; box-shadow:none !important; font-weight:600; border-radius:12px !important; transition:background .18s ease, transform .14s ease; }
  .tg-admin-root .ant-btn-primary:not([disabled]):hover { background:${t.accentTx} !important; transform:translateY(-1px); box-shadow:none !important; }
  .tg-admin-root .ant-btn-default { border-color:${t.border2} !important; color:${t.text} !important; box-shadow:${t.sh} !important; border-radius:12px !important; }
  .tg-admin-root .ant-btn-default:not([disabled]):hover { border-color:${t.accent} !important; color:${t.accentTx} !important; transform:translateY(-1px); }
  .tg-admin-root .ant-segmented { background:${t.panel} !important; border:1px solid ${t.border}; border-radius:12px; padding:4px; box-shadow:${t.sh}; }
  .tg-admin-root .ant-segmented-item-selected { background:${t.accent} !important; color:#fff !important; border-radius:9px; box-shadow:0 6px 14px -6px ${t.accent}; }
  .tg-admin-root .ant-segmented-item-label { color:${t.text2}; font-weight:600; padding:3px 8px; }
  .tg-admin-root .ant-segmented-item-selected .ant-segmented-item-label { color:#fff !important; }

  /* ===== status pill remaps (legacy tabs) → quiet tinted ===== */
  .tg-admin-root .text-blue-400, .tg-admin-root .text-blue-500, .tg-admin-root .text-blue-600 { color:${t.accentTx} !important; }
  .tg-admin-root .rounded-full.text-xs { font-weight:600 !important; padding:3px 10px !important; }
  .tg-admin-root .bg-green-500\\/20, .tg-admin-root .bg-green-100 { background:${isDark ? 'rgba(63,194,127,.14)' : '#e6f6ee'} !important; }
  .tg-admin-root .text-green-400, .tg-admin-root .text-green-600, .tg-admin-root .text-green-700 { color:${t.okTx} !important; }
  .tg-admin-root .bg-yellow-500\\/20, .tg-admin-root .bg-yellow-100, .tg-admin-root .bg-orange-500\\/20, .tg-admin-root .bg-orange-100 { background:${isDark ? 'rgba(224,176,74,.14)' : '#fdf3e2'} !important; }
  .tg-admin-root .text-yellow-400, .tg-admin-root .text-yellow-600, .tg-admin-root .text-yellow-700, .tg-admin-root .text-orange-400, .tg-admin-root .text-orange-600 { color:${t.warnTx} !important; }
  .tg-admin-root .bg-red-500\\/20, .tg-admin-root .bg-red-100 { background:${isDark ? 'rgba(240,115,108,.14)' : '#fdecec'} !important; }
  .tg-admin-root .text-red-400, .tg-admin-root .text-red-600, .tg-admin-root .text-red-700 { color:${t.badTx} !important; }
  .tg-admin-root .bg-purple-500\\/20, .tg-admin-root .bg-purple-100, .tg-admin-root .bg-gray-500\\/20, .tg-admin-root .bg-gray-100, .tg-admin-root .bg-cyan-500\\/20, .tg-admin-root .bg-cyan-100 { background:${t.panel2} !important; box-shadow:inset 0 0 0 1px ${t.border} !important; }
  .tg-admin-root .text-purple-400, .tg-admin-root .text-purple-600, .tg-admin-root .text-cyan-400, .tg-admin-root .text-gray-400 { color:${t.text2} !important; }

  /* ===== org cell + icon status (AdminUI) ===== */
  .tg-org { display:flex; align-items:center; gap:12px; }
  .tg-org-ava { height:36px; width:36px; border-radius:11px; background:${t.accentWeak}; color:${t.accentTx}; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
  .tg-org b { font-weight:600; display:block; font-size:14px; color:${t.text}; line-height:1.3; }
  .tg-org span { color:${t.text3}; font-size:12.5px; line-height:1.3; }
  .tg-plan { font-size:14px; font-weight:500; color:${t.text2}; }
  .tg-bill { font-size:14px; color:${t.text2}; white-space:nowrap; }
  .tg-bill small { color:${t.text3}; font-size:12.5px; margin-left:2px; }
  .tg-bill small.tg-bill-warn { color:${t.warnTx}; }
  /* status = icon + colored text */
  .tg-st { display:inline-flex; align-items:center; gap:8px; font-size:14px; font-weight:600; }
  .tg-st svg { width:17px; height:17px; flex-shrink:0; }
  .tg-st.ok{color:${t.okTx};} .tg-st.info{color:${t.dotInfo};} .tg-st.warn{color:${t.warnTx};} .tg-st.bad{color:${t.badTx};} .tg-st.off{color:${t.text3};}

  /* row action buttons */
  .tg-actions { opacity:1; }
  .tg-act { background:none; border:0; cursor:pointer; color:${t.text3}; padding:7px; border-radius:9px; display:inline-flex; transition:background .16s, color .16s, transform .14s; }
  .tg-act:hover { background:${t.hair}; color:${t.text}; transform:translateY(-1px); }
  .tg-act.tg-act-danger:hover { color:${t.badTx}; background:${isDark ? 'rgba(240,115,108,.12)' : '#fdecec'}; }

  /* ===== AntD table — airy, animated ===== */
  .tg-admin-root .custom-table .ant-table { background:transparent !important; }
  .tg-admin-root .custom-table .ant-table-thead > tr > th {
    background:transparent !important; color:${t.text2} !important; font-size:13.5px !important; font-weight:600 !important;
    letter-spacing:0 !important; text-transform:none !important; border-bottom:1px solid ${t.hair} !important; padding:14px 16px !important; white-space:nowrap !important;
  }
  .tg-admin-root .custom-table .ant-table-thead > tr > th::before { display:none !important; }
  .tg-admin-root .custom-table .ant-table-tbody > tr > td { border-bottom:1px solid ${t.hair} !important; padding:16px !important; color:${t.text} !important; }
  .tg-admin-root .custom-table .ant-table-tbody > tr { transition:background .18s ease, box-shadow .18s ease; animation:tgFade .4s ease both; }
  .tg-admin-root .custom-table .ant-table-tbody > tr:hover > td { background:${t.panel2} !important; }
  .tg-admin-root .custom-table .ant-table-tbody > tr.ant-table-row-selected > td { background:${t.accentWeak} !important; }
  /* header icon tiles */
  .tg-th { display:inline-flex; align-items:center; gap:9px; }
  .tg-th-ico { height:26px; width:26px; border-radius:8px; display:inline-flex; align-items:center; justify-content:center; flex-shrink:0; }
  .tg-th-ico svg { width:14px; height:14px; }
  .tg-th-ico.i1{background:${t.blueBg};color:${t.blueTx}} .tg-th-ico.i2{background:${t.orangeBg};color:${t.orangeTx}}
  .tg-th-ico.i3{background:${t.tealBg};color:${t.tealTx}} .tg-th-ico.i4{background:${t.purpleBg};color:${t.purpleTx}} .tg-th-ico.i5{background:${t.accentWeak};color:${t.accentTx}}
  /* fixed columns */
  .tg-admin-root .custom-table .ant-table-thead .ant-table-cell-fix-right { background:${t.shell} !important; }
  .tg-admin-root .custom-table .ant-table-tbody .ant-table-cell-fix-right { background:${t.shell} !important; }
  .tg-admin-root .custom-table .ant-table-tbody > tr:hover .ant-table-cell-fix-right { background:${t.panel2} !important; }
  /* only show the fixed-right separating shadow when there is actual scroll
     (AntD adds .ant-table-ping-right) — otherwise it looks like a stray line */
  .tg-admin-root .custom-table .ant-table-cell-fix-right::after { box-shadow: none !important; }
  .tg-admin-root .custom-table .ant-table-ping-right .ant-table-cell-fix-right-first::after,
  .tg-admin-root .custom-table .ant-table-ping-right .ant-table-cell-fix-right::after { box-shadow: inset -10px 0 8px -8px ${isDark ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.07)'} !important; }
  /* subtle horizontal scrollbar for the table; kill the sticky scrollbar track */
  .tg-admin-root .custom-table .ant-table-body::-webkit-scrollbar,
  .tg-admin-root .custom-table .ant-table-content::-webkit-scrollbar { height: 8px; }
  .tg-admin-root .custom-table .ant-table-body::-webkit-scrollbar-thumb,
  .tg-admin-root .custom-table .ant-table-content::-webkit-scrollbar-thumb { background: ${t.border2}; border-radius: 8px; }
  .tg-admin-root .custom-table .ant-table-sticky-scroll { display: none !important; }
  .tg-admin-root .custom-table .ant-table-sticky-holder { background: ${t.shell} !important; }
  .tg-admin-root .ant-checkbox-inner { border-radius:6px !important; border-color:${t.border2} !important; }
  .tg-admin-root .ant-checkbox-checked .ant-checkbox-inner { background:${t.accent} !important; border-color:${t.accent} !important; }

  /* pagination */
  .tg-admin-root .ant-pagination-item, .tg-admin-root .ant-pagination-item-link { border-radius:10px !important; border-color:${t.border2} !important; }
  .tg-admin-root .ant-pagination-item-active { border-color:${t.accent} !important; background:${t.accentWeak} !important; }
  .tg-admin-root .ant-pagination-item-active a { color:${t.accentTx} !important; }

  /* modals */
  .tg-admin-root .fixed.inset-0 > .bg-black\\/80, .tg-admin-root [class*="max-w-"].bg-black\\/80 { background:${t.panel} !important; border:1px solid ${t.border} !important; border-radius:20px !important; box-shadow:${t.shLift} !important; color:${t.text} !important; }
  .tg-admin-root .ant-modal-content { border-radius:20px !important; background:${t.panel} !important; box-shadow:${t.shLift} !important; }
  .tg-admin-root .ant-modal-header { background:transparent !important; }
  .tg-admin-root .ant-modal-title { color:${t.text} !important; }
  `;
};

/** Renders the scoped stylesheet. Drop once per admin page. */
export const AdminDesignSystem = () => {
  const { mode } = useTheme();
  return <style>{getAdminDesignSystem(mode === 'dark')}</style>;
};

export default AdminDesignSystem;
